import { DEFAULT_OG_IMAGE, SITE_NAME, absolute, pageTitle } from '@/lib/site'

/*
 * Per-route metadata.
 *
 * React 19 hoists `<title>`, `<meta>` and `<link>` rendered anywhere in the
 * tree into <head>, so this needs no helmet library. That covers a browser; it
 * does not cover a crawler that never runs JS, which is what the prerender step
 * in `tools/seo/prerender.ts` exists for.
 *
 * Tag shape is copied from the capture: og:title / og:description / og:image /
 * og:type plus twitter:title / twitter:description / twitter:image and
 * twitter:card = summary_large_image.
 */
export interface SeoProps {
  title: string
  description?: string | null
  path: string
  image?: string | null
  type?: 'website' | 'article'
  publishedTime?: string | null
  author?: string | null
  noindex?: boolean
  children?: React.ReactNode
}

export function Seo({
  title,
  description,
  path,
  image,
  type = 'website',
  publishedTime,
  author,
  noindex,
  children,
}: SeoProps) {
  const full = pageTitle(title)
  const url = absolute(path)
  const img = absolute(image || DEFAULT_OG_IMAGE)

  return (
    <>
      <title>{full}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={full} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={url} />
      {img && <meta property="og:image" content={img} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={full} />
      {description && <meta name="twitter:description" content={description} />}
      {img && <meta name="twitter:image" content={img} />}

      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && author && <meta property="article:author" content={author} />}

      {children}
    </>
  )
}
